import type { AppState, Player, Snapshot, Spell } from "../types";
import { e, button, formatTime } from "../dom";
import { icon } from "../icons";
import { SPELL_COPY, purchaseStatus, SLOT_KEYS } from "../spells";
import { spellRank, upgradeStatus, upgradeDetail } from "../upgrades";
import { collapseWarningCopy } from "../collapse-warning";
import { colorRoster } from "../player-colors";
import { canReorderSpells } from "../spell-reorder";
import {
  avatar,
  gold,
  localPlayer,
  viewedPlayer,
  spellSlots,
  teamMark,
  teamName,
  toolbar,
} from "./shared";

export function standings(snapshot: Snapshot, localId: number | null): string {
  const roster = snapshot.players.map((player) => player.id);
  const row = (p: Player, rank: number) =>
    `<tr class="${p.id === localId ? "is-you" : ""}"><td>${rank}</td><td><span class="standing-player">${avatar(p.id, true, roster)}${teamMark(p.team)}<span>${e(p.name)}</span>${p.bot ? '<span class="badge">Bot</span>' : ""}${p.id === localId ? '<span class="badge">You</span>' : ""}</span></td><td>${p.round_wins}</td><td>${p.eliminations}</td><td>${p.gold}</td></tr>`;
  const sorted = [...snapshot.players].sort(
    (a, b) =>
      b.round_wins - a.round_wins ||
      b.eliminations - a.eliminations ||
      a.id - b.id,
  );
  return `<table class="standings"><thead><tr><th scope="col">#</th><th scope="col">Wizard</th><th scope="col">Wins</th><th scope="col">Eliminations</th><th scope="col">Gold</th></tr></thead><tbody>${
    snapshot.mode === "teams"
      ? [...snapshot.team_scores]
          .sort((a, b) => b.round_wins - a.round_wins)
          .map(
            (team, index) =>
              `<tr class="team-standing"><th colspan="5">${teamMark(team.team)}${teamName(team.team)} <span>${team.round_wins} round wins</span></th></tr>${sorted
                .filter((p) => p.team === team.team)
                .map((p) => row(p, index + 1))
                .join("")}`,
          )
          .join("")
      : sorted.map((p, i) => row(p, i + 1)).join("")
  }</tbody></table>`;
}
function spectator(state: AppState): string {
  const followed = viewedPlayer(state);
  const me = localPlayer(state);
  return `<div class="spectator-bar beveled"><span class="spectator-label">${icon("eye")}${me ? "Eliminated" : "Watching"}</span><button class="icon-button previous" data-action="follow-prev" aria-label="Follow previous wizard">${icon("chevron")}</button><button data-action="follow-list" class="follow-name">${followed ? `${teamMark(followed.team)}${e(followed.name)}` : "Arena view"}</button><button class="icon-button" data-action="follow-next" aria-label="Follow next wizard">${icon("chevron")}</button></div>`;
}
function compactScores(snapshot: Snapshot): string {
  if (snapshot.mode === "teams")
    return snapshot.team_scores
      .map(
        (t) =>
          `<span class="team-score">${teamMark(t.team)}${teamName(t.team)}<b>${t.round_wins}</b><small data-team-alive="${t.team}">${snapshot.players.filter((p) => p.team === t.team && p.alive).length} alive</small></span>`,
      )
      .join("");
  return `<span class="alive-count"><b data-alive>${snapshot.players.filter((p) => p.alive).length}</b> / ${snapshot.players.length} alive</span>`;
}
export function matchView(state: AppState): string {
  const s = state.snapshot!;
  if (s.phase === "shop") return shopView(state);
  if (s.phase === "match_over") return resultView(state, true);
  const me = localPlayer(state),
    viewed = viewedPlayer(state);
  return `<main class="match-screen" aria-label="Warlock arena">${toolbar("Menu")}<header class="match-header beveled"><span class="round-indicator">Round <b>${s.round}</b><span>/ ${s.total_rounds}</span></span><span class="hud-divider"></span>${compactScores(s)}<span class="hud-divider"></span><span data-round-time>${formatTime(s.round_tick / s.tick_rate)}</span></header>
    <div class="elimination-feed" data-feed aria-live="polite"></div><div class="collapse-warning ${s.collapse_warning == null || s.phase !== "combat" ? "hidden" : ""}" data-collapse role="status" aria-live="polite" aria-atomic="true">Watch your step<span data-collapse-message>${collapseWarningCopy(s)}</span></div>
    ${s.phase === "countdown" ? `<div class="round-intro"><span class="eyebrow">${s.round === 1 ? "Eight wizards. One small island." : "Back into the fire"}</span><h1>Round ${s.round}</h1><b class="countdown-number" data-phase-time>${Math.ceil(s.phase_ticks_remaining / s.tick_rate)}</b>${s.round === 1 && me ? "<p>Move with WASD · Aim with your mouse · Cast with left click</p>" : ""}</div>` : ""}
    ${s.phase === "round_result" ? `<section class="round-result panel"><span class="eyebrow">Round ${s.round} complete</span><h1>${winnerCopy(s, state.matchPlayerId)}</h1><p>${s.round < s.total_rounds ? "A moment to catch your breath. The spell shop is next." : "The arena has its champions."}</p>${me ? `<div class="result-wallet">${gold(me.gold)}<span>In your wallet</span></div>` : ""}<span class="muted">Next in <b data-phase-time>${Math.ceil(s.phase_ticks_remaining / s.tick_rate)}</b></span></section>` : ""}
    <div class="combat-bottom">${me?.alive ? '<div class="controls-hint"><span><kbd>W</kbd><span><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd></span></span><span>Move<br><small>Mouse to aim</small></span></div>' : "<div></div>"}<div class="combat-build">${!me?.alive ? spectator(state) : ""}${spellSlots(viewed)}${viewed ? `<span class="build-owner">${teamMark(viewed.team)}${e(viewed.name)}${!me?.alive ? " · current spells" : ""}</span>` : ""}</div>${me ? `<span class="combat-wallet" role="status" aria-label="Gold"><span data-wallet>${me.gold}</span></span>` : "<div></div>"}</div></main>`;
}
export function winnerCopy(s: Snapshot, localId: number | null): string {
  if (!s.winners.length) return "A glorious draw";
  if (localId != null && s.winners.includes(localId))
    return s.mode === "teams"
      ? "Your team takes the round"
      : "You take the round";
  if (s.mode === "teams")
    return `${teamName(s.players.find((p) => p.id === s.winners[0])?.team)} takes the round`;
  return `${e(s.players.find((p) => p.id === s.winners[0])?.name ?? "A wizard")} takes the round`;
}
export interface RewardSummary {
  saved: number;
  combat: number;
  grant: number;
  spent: number;
  total: number;
}
export function rewardSummary(s: Snapshot, me: Player): RewardSummary {
  const relevant = (
    s.gold_receipts ??
    s.events
      .filter((ev) => ev.kind.type === "gold_awarded")
      .map((ev) => ev.kind)
  ).filter((receipt) => receipt.player === me.id);
  const previousRound = s.phase === "shop" ? s.round - 1 : s.round;
  const combat = relevant
    .filter((ev) => ev.victim != null && ev.round === previousRound)
    .reduce((n, ev) => n + Number(ev.amount), 0);
  const grant = relevant
    .filter((ev) => ev.victim == null && ev.round === s.round)
    .reduce((n, ev) => n + Number(ev.amount), 0);
  const spent = [
    ...(s.purchase_receipts ??
      s.events
        .filter((ev) => ev.kind.type === "purchased")
        .map((ev) => ev.kind.receipt)),
    ...(s.upgrade_receipts ??
      s.events
        .filter((ev) => ev.kind.type === "upgraded")
        .map((ev) => ev.kind.receipt)),
  ]
    .filter((receipt) => receipt.player === me.id && receipt.round === s.round)
    .reduce((n, receipt) => n + Number(receipt.cost), 0);
  return {
    saved: Math.max(0, me.gold - combat - grant + spent),
    combat,
    grant,
    spent,
    total: me.gold,
  };
}
export function shopView(
  state: AppState,
  selected = state.selectedSlot,
): string {
  const s = state.snapshot!,
    me = localPlayer(state);
  const summary = me ? rewardSummary(s, me) : null;
  const editableSlots = canReorderSpells(state);
  const spellCard = (spell: Spell) => {
    const copy = SPELL_COPY[spell],
      offer = state.catalog.find((o) => o.spell === spell);
    if (!offer) return "";
    const owned = me?.loadout.includes(spell);
    const rank = spellRank(me, spell);
    const status = owned
      ? upgradeStatus(
          offer,
          me,
          s.round,
          state.pendingRequest != null || state.pendingReorder,
        )
      : purchaseStatus(
          offer,
          me,
          state.pendingRequest != null || state.pendingReorder,
        );
    const cooldown =
      offer.upgrades?.find((upgrade) => upgrade.rank === rank)?.config
        .cooldown_seconds ?? offer.cooldownSeconds;
    const damage =
      rank === 0
        ? offer.damage
        : offer.upgrades?.find((upgrade) => upgrade.rank === rank)?.damage;
    const nextDamage = owned
      ? offer.upgrades?.find((upgrade) => upgrade.rank === rank + 1)?.damage
      : undefined;
    const damageCopy =
      damage != null && damage > 0
        ? `<span class="spell-damage">${Number(damage.toFixed(2))}${nextDamage != null && nextDamage !== damage ? ` → ${Number(nextDamage.toFixed(2))}` : ""} damage</span>`
        : "";
    return `<article class="spell-card ${owned ? "learned" : ""} ${spell === "fireball" ? "core-spell" : ""}"><div class="spell-card-top"><span class="spell-art">${icon(copy.icon)}</span><div><h3>${copy.name}${owned ? `<span class="rank-label">${rank ? `Rank ${rank === 1 ? "I" : "II"}` : "Learned"}</span>` : ""}</h3><span class="spell-cooldown">${cooldown.toFixed(1)}s cooldown</span></div></div><p>${copy.description}</p><div class="spell-detail ${owned ? "upgrade-detail" : ""}">${owned ? e(upgradeDetail(offer, me) || copy.detail) : copy.detail}${damageCopy}</div>${button(owned ? "upgrade" : "buy", e(status.label), { primary: !status.disabled, disabled: status.disabled || !state.connected, extra: `data-spell="${spell}"` })}</article>`;
  };
  return `<main class="panel-screen shop-screen">${toolbar("Menu")}<section class="panel shop-panel" aria-labelledby="shop-title"><header class="panel-header"><div><span class="eyebrow">Warlock</span><h1 id="shop-title">Spell shop</h1><p>Round ${s.round} / ${s.total_rounds}<span class="dot-separator">·</span>Next round in <b data-phase-time>${formatTime(s.phase_ticks_remaining / s.tick_rate)}</b></p></div>${me ? gold(me.gold, true) : `<span class="observer-chip">${icon("eye")}Observer</span>`}</header>
    <div class="shop-body"><aside class="shop-summary"><section class="receipt"><h3>Round ${Math.max(1, s.round - 1)} complete</h3>${button("scoreboard", "View results", { small: true })}${summary ? `<dl><div><dt>Saved gold</dt><dd data-receipt="saved">${summary.saved}</dd></div><div><dt>Combat rewards</dt><dd data-receipt="combat">+${summary.combat}</dd></div><div><dt>Round grant</dt><dd data-receipt="grant">+${summary.grant}</dd></div>${summary.spent ? `<div><dt>Spells & upgrades</dt><dd>−${summary.spent}</dd></div>` : ""}<div class="receipt-total"><dt>Available</dt><dd>${summary.total}</dd></div></dl>` : '<p class="muted">The wizards are preparing their next move.</p>'}</section><div class="shop-identity">${avatar(state.matchPlayerId ?? state.playerId, false, colorRoster(state))}<div><strong>${e(state.name)}</strong><span>${me ? "Spells last this match" : "Watching the match"}</span></div></div><p class="shop-note">${me ? `Choose an empty slot for your next spell. Drag any learned spell between the four slots to move it or swap controls. Upgrade your learned spells as new ranks open. Each card shows the next benefit and price.` : "Observers follow the same round flow. Purchases and readiness belong to competitors."}</p></aside>
    <div class="shop-catalog">${state.catalog.some((offer) => offer.spell === "fireball") ? `<section class="core-upgrade">${spellCard("fireball")}</section>` : ""}<section><h2>Escape <span>Stay one step ahead</span></h2><div class="spell-grid escape-grid">${(["dash", "teleport", "shield"] as Spell[]).map(spellCard).join("")}</div></section><section><h2>Offensive <span>Make a little trouble</span></h2><div class="spell-grid offensive-grid">${(["meteor", "dome"] as Spell[]).map(spellCard).join("")}</div></section><div class="your-spells"><h2>${me ? "Your spells" : "Spell slots"}${me && me.loadout.some((v) => !v) ? `<span>Next spell → ${SLOT_KEYS[selected]}</span>` : ""}</h2>${spellSlots(me, editableSlots, selected, editableSlots, state.pendingReorder)}</div></div></div>
    <footer class="panel-footer"><span class="muted">${me ? "Unspent gold carries over" : "Watching · Purchases are read-only"}</span><div class="ready-area"><span data-ready-count>${s.players.filter((p) => p.ready).length} / ${s.players.length} ready</span>${me ? button("ready", me.ready ? `${icon("check")}Ready · edit build` : "Ready", { primary: !me.ready, disabled: state.pendingRequest != null || state.pendingReorder || !state.connected }) : '<span class="muted">Waiting for the next round</span>'}</div></footer></section></main>`;
}
export function resultView(state: AppState, final = true): string {
  const s = state.snapshot!;
  const ownWin =
    state.matchPlayerId != null && s.winners.includes(state.matchPlayerId);
  return `<main class="panel-screen">${toolbar("Menu")}<section class="panel results-panel"><header class="results-header"><span class="eyebrow">${final ? "The final embers" : `Round ${s.round} standings`}</span>${icon("crown", "trophy")}<h1>${final ? (ownWin ? "Victory is yours" : "A match well fought") : "The standings"}</h1><p>${final ? `${s.total_rounds} rounds of beautiful chaos.` : "Round wins decide the match. Elimination rewards buy your spells."}</p></header>${standings(s, state.matchPlayerId)}<footer class="panel-footer">${button("leave", "Leave match")}${state.room?.kind === "friends" ? button("back-room", "Back to room", { primary: true }) : button("play-again", "Play again", { primary: true })}</footer></section></main>`;
}
